import { useState } from 'react'

import { Icon, useFvNavigate } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'
import type { EquipmentType } from '@fv/models/equipment'

import { HideForVendor } from '@/features/auth/HasRole'
import { quoteFuncs } from '@/features/quote/quoteFuncs'
import {
  VendorMarkupDescription,
  VendorShipmentIcon,
} from '@/features/shipment-list/VendorShipmentIndicator'
import { useCancelLoad } from '@/hooks/shipments'
import arrayOfLength from '@/utils/arrayOfLength'

import InfoBox from '../InfoBox'
import { List, ListItem } from '../List'
import { RouteStopDate } from '../QuoteRequest/RouteStopDate'
import TruckSelector from '../shared/TruckSelector'
import { ItemSummaryPanel } from './ItemSummaryPanel'
import { LoadCancellationPanel } from './LoadCancellationPanel'
import { LoadSummaryActions, LoadSummaryDetails } from './LoadSummaryDetails'
import { RouteSummary } from './RouteSummary'
import { TruckloadActions, TruckloadSummary } from './TruckloadSummary'

type Props = {
  activeIndex?: number
  equipmentType?: EquipmentType
  hasAwardedEquipment?: boolean
  isBooking?: boolean
  isEditingLoad?: boolean
  load?: FullShipment
  numberOfTrucks?: number
  quote?: Quote
  hideEdit?: boolean
  onDateEdit?: (sequence: number, date?: Date | null) => void
  selectTruck?: (i: number) => void
}

// TODO: parcel icon
export const LoadSummary = ({
  activeIndex = 0,
  equipmentType,
  hasAwardedEquipment,
  isBooking,
  isEditingLoad,
  load,
  numberOfTrucks = 1,
  quote,
  hideEdit = false,
  onDateEdit,
  selectTruck,
}: Props) => {
  const [isShowingItems, setShowingItems] = useState(false)
  const [isShowingCancel, setShowingCancel] = useState(false)
  const [editStopDateSequence, setEditStopDateSequence] = useState(-1)
  const navigate = useFvNavigate()
  const cancelLoad = useCancelLoad(load?.loadId)
  const isCanceling = cancelLoad.isPending || cancelLoad.isSuccess
  const allowDateEdits = !!onDateEdit

  if (!load) {
    return (
      <div className="flex p-6 flex-col overflow-hidden border-r border-fv-blue-150 bg-fv-blue-50 min-h-0 h-full">
        <InfoBox icon="sync">Loading shipment...</InfoBox>
      </div>
    )
  }

  const { locations, refNums, workflow } = load
  const showItems = () => setShowingItems(true)

  function cancelRequest(isAllLoads: boolean, redirect: string) {
    if (isCanceling) return
    cancelLoad.mutate(isAllLoads, {
      onSuccess: () => navigate(redirect),
    })
  }

  return (
    <div className="flex p-6 min-h-0 h-full flex-col overflow-auto border-r border-fv-blue-150 bg-fv-blue-50">
      <List bare className="[scrollbar-gutter:stable]">
        {numberOfTrucks > 1 && (
          <ListItem className="">
            <div className="flex items-center gap-x-1">
              <span className="mr-1">Truck</span>

              {arrayOfLength(numberOfTrucks).map(i => (
                <TruckSelector
                  active={i === activeIndex}
                  key={i}
                  selectTruck={() => selectTruck?.(i)}
                  truckNumber={i + 1}
                />
              ))}
            </div>
          </ListItem>
        )}

        {workflow === 'truckload' && !!refNums?.length && (
          <ListItem className="b1200:first:pl-0">
            <Icon icon="hashtag" className="text-fv-gray-dark" />
            <span>{refNums.map(n => n.value).join(' / ')}</span>
          </ListItem>
        )}

        <LoadSummaryDetails load={load} showItems={showItems} />

        <TruckloadSummary
          equipmentType={equipmentType}
          isBooking={isBooking}
          load={load}
          showItems={showItems}
        />
      </List>

      <RouteSummary
        numberOfTrucks={numberOfTrucks}
        load={load}
        editOptions={
          allowDateEdits && isEditingLoad
            ? [
                quoteFuncs.isSpotWorkflow(quote) ? 'all' : 'origin',
                seq => setEditStopDateSequence(seq),
              ]
            : undefined
        }
      />
      <HideForVendor>
        {load.createdBy?.vendorId && (
          <List bare>
            <ListItem>
              <VendorShipmentIcon />
              <span>Vendor shipment</span>
            </ListItem>
            <ListItem>
              <VendorMarkupDescription load={load} />
            </ListItem>
          </List>
        )}
      </HideForVendor>
      <List lcp0 className="mt-auto">
        {!hideEdit && <LoadSummaryActions load={load} />}

        <TruckloadActions
          cancelRequest={cancelRequest}
          hasAwardedEquipment={hasAwardedEquipment}
          isBooking={isBooking}
          isCanceling={isCanceling}
          isEditingLoad={isEditingLoad}
          load={load}
          openCancellationPanel={() => setShowingCancel(true)}
        />
      </List>

      <ItemSummaryPanel
        activeIndex={activeIndex}
        isOpen={isShowingItems}
        load={load}
        numberOfTrucks={numberOfTrucks}
        setOpen={setShowingItems}
      />

      <LoadCancellationPanel
        cancelRequest={cancelRequest}
        hasAwardedEquipment={hasAwardedEquipment}
        isCanceling={isCanceling}
        isOpen={isShowingCancel}
        numberOfTrucks={numberOfTrucks}
        setOpen={setShowingCancel}
      />

      {allowDateEdits && (
        <RouteStopDate
          onClose={() => setEditStopDateSequence(-1)}
          isOpen={editStopDateSequence >= 0}
          sequence={editStopDateSequence}
          onChange={({ stopDate }) =>
            onDateEdit(editStopDateSequence, stopDate)
          }
          showStopDetailConfig={false}
          stops={locations}
        />
      )}
    </div>
  )
}
